import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, NotificationManagerContext, Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { Input } from 'src/components/ConnectedForm'
import { useCreateInitiative, useOrganization } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'

const InitiativeCreate = ({
   buttonColor,
   buttonTextColor,
   initiativeTypeId,
   onCancelButtonClick,
   onCreate
}) => {
   const [currentInputCharLength, setCurrentInputCharLength] = useState(0)
   const theme = useContext(ThemeContext)
   const { showNotification } = useContext(NotificationManagerContext)

   const { organization } = useOrganization()
   const { createInitiative } = useCreateInitiative()
   const initiativeType = organization.initiativeTypes.find(t => t.id === initiativeTypeId)
   let initiativeNameLabel = 'name'
   let submitButtonName = 'Create'

   if (initiativeType.class === 'discussion' || initiativeType.class === 'post') {
      initiativeNameLabel = 'title'
      submitButtonName = 'Next'
   }

   const handleCreateInitiative = async (values, actions) => {
      try {
         const result = await createInitiative({
            typeId: initiativeTypeId,
            ...values
         })

         if (onCreate) {
            onCreate(result.data.createInitiative)
         }
      } catch (err) {
         showNotification({
            message: err?.graphQLErrors?.map(e => e.message).join(', ') || err.message,
            title: 'Error',
            type: 'error'
         })
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <View>
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="15px"
            borderBottom="1px solid #eee">
            <Text size="3" weight="medium">
               {`Create New ${initiativeType.nameSingular}`}
            </Text>
         </View>
         <Formik
            initialValues={{ name: '' }}
            onSubmit={handleCreateInitiative}
            render={({ handleSubmit, handleChange, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  elementFocusBackgroundColor="#f5f5f5"
                  onSubmit={handleSubmit}>
                  <View padding="15px">
                     <View>
                        <Text size="2" color="#444" weight="medium">
                           {`Please enter a ${initiativeNameLabel} for this new ${initiativeType.nameSingular}`}
                        </Text>
                        <Text size="2" color="#888">
                           {`(${currentInputCharLength} of 120 max characters)`}
                        </Text>
                        <Spacer size="2" />
                        <Input
                           maxLength="120"
                           name="name"
                           size="lg"
                           onChange={(e) => {
                              handleChange(e)
                              setCurrentInputCharLength(e.target.value.length)
                           }}
                           placeholder={`${initiativeType.nameSingular} ${initiativeNameLabel}`}
                           required
                        />
                     </View>
                     <Spacer size="3" />
                     <View display="flex" width="100%" justifyContent="flex-end">
                        <View
                           width="100%"
                           maxWidth="270px"
                           display="flex"
                           justifyContent="flex-end">
                           <Button
                              onClick={onCancelButtonClick}
                              width="50%"
                              name="Cancel"
                              color="#fff"
                              size="md"
                              textColor="#666"
                           />
                           <Button
                              width="50%"
                              type="submit"
                              name={submitButtonName}
                              color={buttonColor || theme.protoSettingsHighlightBackgroundColor}
                              size="md"
                              textColor={buttonTextColor ||
                                 theme.protoSettingsHighlightForegroundColor
                              }
                              mode={isSubmitting ? 'loading' : 'normal'}
                           />
                        </View>
                     </View>
                  </View>
               </Form>
            )}
         />
      </View>
   )
}

InitiativeCreate.propTypes = {
   buttonColor: PropTypes.string,
   buttonTextColor: PropTypes.string,
   initiativeTypeId: PropTypes.string.isRequired,
   onCancelButtonClick: PropTypes.func,
   onCreate: PropTypes.func
}

InitiativeCreate.defaultProps = {
   buttonColor: undefined,
   buttonTextColor: undefined,
   onCancelButtonClick: undefined,
   onCreate: undefined
}

export default InitiativeCreate
